import { getStoreOrigin, isEmbeddedInStore, notifyStoreParent } from "@utils/embeddedStore";
// Shared helpers for Adyen components

import { createCardPayment, getCardDetails } from "@api";
import { buildIframeApplePayMerchantValidation } from "./applePayMerchantValidation";
import {
  CARD_DETAILS_TIMEOUT_MS,
  createCardTypeResolver,
} from "./cardTypeResolver";

export { CORP_TOKENS } from "./cardTypeResolver";

// On mobile, scroll the focused card field up to the top of the page so there is a
// full screen of space above the keyboard. The card fields are cross-origin iframes
// (native focus doesn't bubble), so this is driven by Adyen's card onFocus for those
// and a native focusin listener for the billing-address inputs. We read
// document.activeElement (the focused iframe/input) and scroll its field wrapper.
// Desktop is left untouched.
//
// The scroll starts immediately (next frame) so it reads as a single motion as the
// keyboard opens, instead of iOS scrolling the field into view first and us then
// yanking it to the top a beat later. A delayed second pass re-asserts the top
// position once the keyboard has settled, in case iOS ran its own scroll afterwards -
// a no-op if the field is already there.
let fieldScrollTimer = null;
let fieldScrollFrame = 0;
// While a payment failure is being revealed, field focus must not scroll:
// Adyen restores focus to the last card field as it resets the form, and
// that scroll would take the page straight back down from the message.
// Ends with the reveal, or as soon as the shopper does anything.
let revealingUntil = 0;
let revealTimers = [];

export function scrollFocusedFieldIntoView() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  if (Date.now() < revealingUntil) return;
  if (window.matchMedia("(min-width: 1024px)").matches) return;
  // Inside a store's frame the frame grows to fit the form, so every field
  // is already in view, and scrolling here would drag the store's own page
  // along with it. A store identifies itself with the origin it passes;
  // other iframes (the embedded account payment) keep the scroll.
  if (isEmbeddedInStore() && getStoreOrigin()) return;

  const scrollToField = (behavior) => {
    const active = document.activeElement;
    if (!active || active === document.body) return;
    const target = active.closest?.(".adyen-checkout__field") || active;
    target.scrollIntoView?.({ block: "start", behavior });
  };

  if (fieldScrollTimer) clearTimeout(fieldScrollTimer);
  cancelAnimationFrame(fieldScrollFrame);
  fieldScrollFrame = requestAnimationFrame(() => {
    fieldScrollFrame = 0;
    scrollToField("smooth");
  });
  fieldScrollTimer = setTimeout(() => {
    fieldScrollTimer = null;
    scrollToField("auto");
  }, 450);
}

function cancelFieldScroll() {
  if (fieldScrollTimer) {
    clearTimeout(fieldScrollTimer);
    fieldScrollTimer = null;
  }
  cancelAnimationFrame(fieldScrollFrame);
  fieldScrollFrame = 0;
}

function endReveal() {
  revealingUntil = 0;
  revealTimers.forEach(clearTimeout);
  revealTimers = [];
}

/** The nearest ancestor that actually scrolls, else the document. */
function scrollerFor(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * Bring a payment failure into view. Adyen renders its failure message at the
 * top of the drop-in, while the Pay button sits in the footer the shopper just
 * pressed; scrolling back up to the card step is what makes the message
 * noticeable. Anchored to the element wrapping the message and the form,
 * with headroom for the heading above it, and scrolled on whichever element
 * scrolls that layout: the document on a phone, an inner panel on desktop.
 */
export function revealPaymentError() {
  if (typeof document === "undefined") return;

  // An embedding store scrolls its own page to the frame on this message;
  // from inside the frame that scroll is at the mercy of the store's page.
  // The in-frame scroll below still runs for the full-page and older-store
  // cases, and targets the same spot, so the two never fight.
  notifyStoreParent({ type: "ontik:express:payment_error" });

  // A field-focus scroll still pending would land after this one and hide
  // the message again; so would the browser's own scroll to a field Adyen
  // refocuses as it resets the form. Drop that focus and hold our handler
  // off until the reveal is done.
  cancelFieldScroll();
  endReveal();
  revealingUntil = Date.now() + 1300;

  const container = () => document.getElementById("adyen-dropin-container");
  const blurAdyenField = () => {
    const el = container();
    const active = document.activeElement;
    if (el && active && el.contains(active)) active.blur?.();
  };
  blurAdyenField();

  const HEADROOM = 120;
  const scrollToStep = (behavior) => {
    const el = container();
    const wrapper = el?.parentElement || el;
    if (!wrapper) return;
    const top = wrapper.getBoundingClientRect().top;
    const panel = scrollerFor(wrapper);
    if (panel) {
      const offset = top - panel.getBoundingClientRect().top + panel.scrollTop;
      panel.scrollTo({ top: Math.max(0, offset - HEADROOM), behavior });
    } else {
      window.scrollTo({ top: Math.max(0, top + window.scrollY - HEADROOM), behavior });
    }
  };

  // Short delay: lets Adyen render its message and a phone keyboard close,
  // either of which cancels a scroll started at the same instant. Then
  // re-asserted once the smooth scroll and any focus restoration are over;
  // a no-op when nothing moved the page in between.
  revealTimers.push(
    setTimeout(() => {
      blurAdyenField();
      scrollToStep("smooth");
    }, 200),
    setTimeout(() => {
      blurAdyenField();
      scrollToStep("auto");
      endReveal();
    }, 1100)
  );

  // The shopper doing anything ends the reveal: no more blurring, no late
  // scroll, and field focus scrolls again while they correct the details.
  // An embedding store cannot see input inside the frame, so it is told.
  const onInteract = () => {
    endReveal();
    notifyStoreParent({ type: "ontik:express:shopper_active" });
    ["pointerdown", "touchstart", "keydown", "wheel"].forEach((t) =>
      document.removeEventListener(t, onInteract, true)
    );
  };
  ["pointerdown", "touchstart", "keydown", "wheel"].forEach((t) =>
    document.addEventListener(t, onInteract, { capture: true, once: true })
  );
}

export function resolveInitData(response, accountType = "payment") {
  if (!response) {
    throw new Error(`Failed to initialize ${accountType} payment`);
  }
  if (response.error) {
    throw new Error(
      typeof response.error === "string"
        ? response.error
        : JSON.stringify(response.error)
    );
  }
  if (!response.data) {
    throw new Error(`Missing payment data for ${accountType}`);
  }
  return response.data;
}

function prepareSubmit({
  incoming,
  disablePaymentRef,
  paymentParameters,
  onCardNotAllowed,
  onError,
  setProcessing,
  authoritativeAmount,
  onStorePaymentDetails,
  instantPaymentTypes = [],
}) {
  const payload = incoming?.data ? incoming.data : incoming;
  const paymentMethodType =
    payload?.paymentMethod?.type ?? incoming?.paymentMethod?.type;
  const isInstantPayment =
    Array.isArray(instantPaymentTypes) &&
    (instantPaymentTypes.includes(paymentMethodType) ||
      paymentMethodType === "paywithgoogle");

  if (disablePaymentRef?.current && !isInstantPayment) {
    console.warn("Rejecting submit: payments are disabled");
    return { ok: false };
  }

  console.log("paymentParameters", paymentParameters);

  if (!paymentParameters?.cardPaymentAllowed) {
    console.warn("Card payments are not allowed for this payment request");
    onCardNotAllowed?.();
    return { ok: false };
  }

  const hasPaymentMethod = !!payload?.paymentMethod;
  const isValid =
    typeof incoming?.isValid === "boolean"
      ? incoming.isValid
      : hasPaymentMethod;

  if (!isValid || !hasPaymentMethod) {
    console.warn("Drop-in data invalid or missing payment method");
    onError?.();
    return { ok: false };
  }

  setProcessing?.(true);
  if (authoritativeAmount !== undefined) {
    onStorePaymentDetails?.(authoritativeAmount);
  }

  return { ok: true, payload };
}

export function isApplePayCancellation(error) {
  return (
    (error?.name === "CANCEL" && error?.message?.includes("ApplePay")) ||
    (String(error).includes("ApplePay") && String(error).includes("CANCEL"))
  );
}

export function normalizeAmount(raw) {
  if (raw == null) return undefined;
  if (typeof raw === "object") return { ...raw };
  const n = Number(raw) || 0;
  return {
    baseAmount: n,
    totalAmount: n,
    totalFeesAmount: 0,
    totalFeesPercentage: 0,
  };
}

/**
 * 3-D Secure challenge window. Adyen's default (02) is a fixed 390x400px,
 * wider than a phone's card box and too short for a bank page reflowed to
 * that width, so the password field and OK button were cut off on mobile.
 * Small screens use the full-size window (05), which fills its container;
 * adyen-overrides.css gives that container a usable height.
 */
export function threeDSChallengeWindowSize(
  isSmallScreen = typeof window !== "undefined" &&
    window.matchMedia("(max-width: 640px)").matches
) {
  return isSmallScreen ? "05" : "02";
}

export function buildCardSection({
  onAmexChange,
  onCardBrandChange,
  onCardTypeChange,
  setBinLookupPending,
  merchantId,
  paymentRequestId,
  checkoutId,
}) {
  // The drop-in's BIN-lookup result, passed along with the card-details request so
  // payment-service can log it next to the lookup's answer.
  let lastBinLookup = null;

  // Corporate/personal is decided by the card-details lookup, with the drop-in's
  // variants as the provisional and fallback signal. See cardTypeResolver.js.
  const cardTypeResolver = createCardTypeResolver({
    onCardTypeChange,
    setBinLookupPending,
    lookup: merchantId
      ? (encryptedCardNumber) =>
          getCardDetails(
            encryptedCardNumber,
            merchantId,
            { paymentRequestId, checkoutId, ...(lastBinLookup ?? {}) },
            { timeoutMs: CARD_DETAILS_TIMEOUT_MS }
          )
      : undefined,
  });

  return {
    card: {
      onFocus: () => scrollFocusedFieldIntoView(),
      onError: () => {},
      onValidationError: () => {},
      challengeWindowSize: threeDSChallengeWindowSize(),
      billingAddressRequired: true,
      billingAddressMode: "partial",
      brands: ["visa", "mc"],
      onBrand: ({ brand }) => {
        if (brand === "amex") {
          onAmexChange?.(true);
        } else {
          onAmexChange?.(false);
        }
        onCardBrandChange?.(brand);
      },
      onBinLookup: (e) => {
        lastBinLookup = {
          paymentMethodVariants: Array.isArray(e?.paymentMethodVariants)
            ? e.paymentMethodVariants
            : undefined,
          detectedBrands: Array.isArray(e?.detectedBrands)
            ? e.detectedBrands
            : Array.isArray(e?.brands)
              ? e.brands.map((b) => (typeof b === "string" ? b : b?.brand)).filter(Boolean)
              : undefined,
          issuingCountryCode: e?.issuingCountryCode ?? undefined,
        };
        cardTypeResolver.onBinLookup(e?.paymentMethodVariants);
      },
      onBinValue: (e) => {
        cardTypeResolver.onBinValue(e?.binValue);
      },
      onChange: (state) => {
        cardTypeResolver.onCardNumberState({
          valid: state?.valid?.encryptedCardNumber === true,
          encryptedCardNumber: state?.data?.paymentMethod?.encryptedCardNumber,
        });
      },
    },
  };
}

export function buildGooglePaySection() {
  return {
    googlepay: {
      allowedCardNetworks: ["VISA", "MASTERCARD"],
      buttonType: "plain",
    },
  };
}

export function buildApplePaySection(paymentParameters, applePayContext = {}) {
  const onValidateMerchant =
    buildIframeApplePayMerchantValidation(applePayContext);

  return {
    applepay: {
      supportedNetworks: ["visa", "masterCard"],
      totalPriceLabel: "Total",
      countryCode: "GB",
      amount: {
        value: (paymentParameters?.amount || 0) * 100,
        currency: "GBP",
      },
      ...(onValidateMerchant && { onValidateMerchant }),
    },
  };
}

// ---------------------------------------------------------------------------
// Express checkout (WooCommerce iframe) — Google Pay / Apple Pay configs
// ---------------------------------------------------------------------------

/**
 * Build the Google Pay config for WooCommerce express checkout.
 * When running inside an iframe, adds dynamic shipping callbacks that
 * communicate with the parent page via postMessage.
 */
export function buildGooglePayExpressSection({
  isInIframe,
  sendToParent,
  waitForParentResponse,
  shippingValidRef,
  currentTotalRef,
}) {
  return {
    allowedCardNetworks: ["VISA", "MASTERCARD"],
    buttonType: "plain",
    buttonSizeMode: "fill",
    ...(isInIframe && {
      isExpress: true,
      emailRequired: true,
      callbackIntents: ["SHIPPING_ADDRESS", "SHIPPING_OPTION"],
      shippingAddressRequired: true,
      shippingAddressParameters: { phoneNumberRequired: true },
      shippingOptionRequired: true,
      totalPriceStatus: "ESTIMATED",
      onAuthorized: ({ authorizedEvent }) => {
        const email = authorizedEvent?.email || "";
        const phone = authorizedEvent?.shippingAddress?.phoneNumber || "";
        if (email || phone) {
          sendToParent({ type: "ontik:express:shopper_info", email, phone });
        }
      },
      paymentDataCallbacks: {
        onPaymentDataChanged: async (intermediatePaymentData) => {
          const trigger = intermediatePaymentData.callbackTrigger;
          const addr = intermediatePaymentData.shippingAddress;

          if (trigger === "INITIALIZE" || trigger === "SHIPPING_ADDRESS") {
            const address = {
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              addressLine1: "",
              addressLine2: "",
              city: addr?.locality || "",
              state: addr?.administrativeArea || "",
              postcode: addr?.postalCode || "",
              country: addr?.countryCode?.toUpperCase() || "",
            };
            sendToParent({ type: "ontik:express:address", address });

            try {
              const result = await waitForParentResponse("ontik:express:address_result");

              if (result.valid) {
                shippingValidRef.current = true;
                currentTotalRef.current = parseFloat(result.total);
                const methods = result.shippingMethods || [];
                return {
                  newTransactionInfo: {
                    totalPriceStatus: "FINAL",
                    totalPrice: result.total,
                    currencyCode: "GBP",
                  },
                  newShippingOptionParameters: {
                    defaultSelectedOptionId: methods[0]?.id || "",
                    shippingOptions: methods.map((m) => ({
                      id: m.id,
                      label: m.label,
                      description: "\u00A3" + m.cost,
                    })),
                  },
                };
              } else {
                shippingValidRef.current = false;
                return {
                  error: {
                    reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
                    message: result.message || "Cannot ship to this address.",
                    intent: "SHIPPING_ADDRESS",
                  },
                };
              }
            } catch (err) {
              shippingValidRef.current = false;
              console.error("[ontik-express] Google Pay address validation failed:", err);
              return {
                error: {
                  reason: "OTHER_ERROR",
                  message: "Could not validate address.",
                  intent: "SHIPPING_ADDRESS",
                },
              };
            }
          }

          if (trigger === "SHIPPING_OPTION") {
            const selectedOption = intermediatePaymentData.shippingOptionData?.id;
            sendToParent({
              type: "ontik:express:shipping_selected",
              shippingMethodId: selectedOption,
            });

            try {
              const result = await waitForParentResponse("ontik:express:shipping_result");

              if (result.valid) {
                currentTotalRef.current = parseFloat(result.total);
                return {
                  newTransactionInfo: {
                    totalPriceStatus: "FINAL",
                    totalPrice: result.total,
                    currencyCode: "GBP",
                  },
                };
              } else {
                return {
                  error: {
                    reason: "SHIPPING_OPTION_INVALID",
                    message: result.message || "Shipping method not available.",
                    intent: "SHIPPING_OPTION",
                  },
                };
              }
            } catch (err) {
              console.error("[ontik-express] Google Pay shipping selection failed:", err);
              return {
                error: {
                  reason: "OTHER_ERROR",
                  message: "Could not update shipping.",
                  intent: "SHIPPING_OPTION",
                },
              };
            }
          }

          console.warn("[ontik-express] Google Pay unhandled callback trigger:", trigger);
          return {};
        },
      },
    }),
  };
}

/**
 * Build the Apple Pay config for WooCommerce express checkout.
 * When running inside an iframe, adds shipping contact/method callbacks
 * that communicate with the parent page via postMessage.
 */
export function buildApplePayExpressSection({
  isInIframe,
  sendToParent,
  waitForParentResponse,
  currentTotalRef,
  shippingValidRef,
  adyenAmount,
  clientKey,
  environment,
  applePayConfig,
}) {
  const onValidateMerchant = buildIframeApplePayMerchantValidation({
    isInIframe,
    clientKey,
    environment,
    applePayConfig,
  });

  return {
    supportedNetworks: ["visa", "masterCard"],
    totalPriceLabel: "Total",
    countryCode: "GB",
    amount: adyenAmount,
    isExpress: true,
    ...(isInIframe && {
      requiredShippingContactFields: ["postalAddress", "name", "email", "phone"],
      requiredBillingContactFields: ["postalAddress", "name"],
    }),    
    ...(onValidateMerchant && { onValidateMerchant }),
    // Capture email/phone from the authorized Apple Pay event.
    // Adyen's address mapper drops these fields, so we forward them separately.
    onAuthorized: ({ authorizedEvent }) => {
      const shipping = authorizedEvent?.payment?.shippingContact;
      const billing = authorizedEvent?.payment?.billingContact;
      if (shipping || billing) {
        sendToParent({
          type: "ontik:express:shopper_info",
          email: shipping?.emailAddress || billing?.emailAddress || "",
          phone: shipping?.phoneNumber || billing?.phoneNumber || "",
        });
      }
    },
    onShippingContactSelected: async (resolve, reject, event) => {
      const contact = event.shippingContact;
      const address = {
        firstName: contact.givenName || "",
        lastName: contact.familyName || "",
        email: contact.emailAddress || "",
        phone: contact.phoneNumber || "",
        addressLine1: contact.addressLines?.[0] || "",
        addressLine2: contact.addressLines?.[1] || "",
        city: contact.locality || "",
        state: contact.administrativeArea || "",
        postcode: contact.postalCode || "",
        country: contact.countryCode?.toUpperCase() || "",
      };
      sendToParent({ type: "ontik:express:address", address });

      try {
        const result = await waitForParentResponse("ontik:express:address_result");

        if (result.valid) {
          shippingValidRef.current = true;
          currentTotalRef.current = parseFloat(result.total);
          const methods = result.shippingMethods || [];
          resolve({
            newShippingMethods: methods.map((m) => ({
              identifier: m.id,
              label: m.label,
              detail: "",
              amount: m.cost,
            })),
            newTotal: { label: "Total", amount: result.total, type: "final" },
            newLineItems: [],
          });
        } else {
          shippingValidRef.current = false;
          reject({
            errors: [
              new ApplePayError("shippingContactInvalid", "postalAddress", result.message || "Address not supported."),
            ],
          });
        }
      } catch (err) {
        shippingValidRef.current = false;
        console.error("[ontik-express] Apple Pay address validation failed:", err);
        reject({
          errors: [
            new ApplePayError("shippingContactInvalid", "postalAddress", "Could not validate address."),
          ],
        });
      }
    },
    onShippingMethodSelected: async (resolve, reject, event) => {
      const methodId = event.shippingMethod?.identifier;
      sendToParent({ type: "ontik:express:shipping_selected", shippingMethodId: methodId });

      try {
        const result = await waitForParentResponse("ontik:express:shipping_result");

        if (result.valid) {
          currentTotalRef.current = parseFloat(result.total);
          resolve({
            newTotal: { label: "Total", amount: result.total, type: "final" },
            newLineItems: [],
          });
        } else {
          reject({
            errors: [
              new ApplePayError("shippingContactInvalid", "postalAddress", result.message || "Shipping method not available."),
            ],
          });
        }
      } catch (err) {
        console.error("[ontik-express] Apple Pay shipping selection failed:", err);
        reject({
          errors: [
            new ApplePayError("shippingContactInvalid", "postalAddress", "Could not update shipping."),
          ],
        });
      }
    },
  };
}

export function applyAmexAllowanceToSections(sections, paymentParameters) {
  if (!paymentParameters?.amexPaymentAllowed) return sections;

  if (sections?.card?.brands && !sections.card.brands.includes("amex")) {
    sections.card.brands.push("amex");
  }
  if (
    sections?.googlepay?.allowedCardNetworks &&
    !sections.googlepay.allowedCardNetworks.includes("AMEX")
  ) {
    sections.googlepay.allowedCardNetworks.push("AMEX");
  }
  if (
    sections?.applepay?.supportedNetworks &&
    !sections.applepay.supportedNetworks.includes("amex")
  ) {
    sections.applepay.supportedNetworks.push("amex");
  }
  return sections;
}

export function onSubmitFactory({
  disablePaymentRef,
  paymentParameters,
  onCardNotAllowed,
  onError,
  setProcessing,
  authoritativeAmount,
  onStorePaymentDetails,
  paymentDataRefRef,
  idempotencyKeyRef,
  cardAccountType, // optional static value for card account type
  instantPaymentTypes = [],
}) {
  return async (state, component, actions) => {
    try {
      const result = prepareSubmit({
        incoming: state,
        disablePaymentRef,
        paymentParameters,
        onCardNotAllowed,
        onError,
        setProcessing,
        authoritativeAmount,
        onStorePaymentDetails,
        instantPaymentTypes,
      });
      if (!result.ok) {
        actions.reject();
        return;
      }
      const payload = result.payload;

      // Create payment (previous onSubmit logic)
      const requestBody = {
        ...paymentParameters,
        paymentProvider: "Adyen",
        clientData: JSON.stringify(payload),
      };

      if (idempotencyKeyRef?.current) {
        requestBody.idempotencyKey = idempotencyKeyRef.current;
      }
      if (cardAccountType) {
        requestBody.cardAccountType = cardAccountType;
      }

      const createPaymentResponse = await createCardPayment(requestBody);
      if (paymentDataRefRef) {
        paymentDataRefRef.current = createPaymentResponse;
      }

      if (createPaymentResponse?.tokenExpired) {
        onError?.({ tokenExpired: true });
        return;
      }

      if (createPaymentResponse?.clientData) {
        const parsedClientData = JSON.parse(createPaymentResponse.clientData);
        actions.resolve({
          resultCode: parsedClientData.resultCode,
          action: parsedClientData.action,
          order: parsedClientData.order,
          donationToken: parsedClientData.donationToken,
        });
      } else {
        console.error(
          "Rejecting payment: createCardPayment did not return clientData. Full response: ",
          createPaymentResponse
        );
        actions.reject();
      }
    } catch (err) {
      console.error("onSubmit", err);
      actions.reject();
      setProcessing?.(false);
    }
  };
}

export function getTranslations() {
  return {
    "en-US": {
      zipCode: "Postal code",
    },
  };
}

export function navigateOnPaymentCompletion({
  navigate,
  onSuccess,
  baseUrl,
  paymentId,
  fallbackPath = "/",
  paymentType = "card",
}) {
  onSuccess?.();

  if (!baseUrl) {
    console.warn(
      "navigateOnPaymentCompletion called without baseUrl; falling back to in–app navigation"
    );
    navigate(fallbackPath);
    return;
  }

  try {
    // absolute URL
    // eslint-disable-next-line no-new
    new URL(baseUrl);
    window.location.href = baseUrl;
  } catch {
    // relative URL
    const separator = baseUrl.includes("?") ? "&" : "?";
    const finalUrl = `${baseUrl}${separator}t=${paymentType}&p-id=${paymentId}`;
    navigate(finalUrl);
  }
}

export function buildDropinConfiguration({
  paymentMethodComponents,
  paymentParameters,
  setLoading,
  onAmexChange,
  onCardBrandChange,
  onCardTypeChange,
  setBinLookupPending,
  instantPaymentTypes = [],
  applePayContext,
}) {
  return {
    paymentMethodComponents,
    disableFinalAnimation: true,
    onReady: () => {
      setLoading?.(false);
    },
    onError: () => {},
    instantPaymentTypes,
    paymentMethodsConfiguration: applyAmexAllowanceToSections(
      {
        ...buildCardSection({
          onAmexChange,
          onCardBrandChange,
          onCardTypeChange,
          setBinLookupPending,
          merchantId: paymentParameters?.merchantId,
          // EXPERIMENT(card-details)
          paymentRequestId: paymentParameters?.paymentRequestId,
          checkoutId: paymentParameters?.checkoutId,
        }),
        ...buildGooglePaySection(),
        ...buildApplePaySection(paymentParameters, applePayContext),
      },
      paymentParameters
    ),
  };
}
